import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  PageHeader,
  SkeletonRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import {
  auditLogRowSchema,
  companySummarySchema,
  paginatedSchema,
  roleSchema,
  type AuditLogRow,
  type Role,
} from '@bia-auction/shared-schema';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { ScrollText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { ErrorMsg } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
  auditActionLabel,
  auditEntityTypeLabel,
  roleLabel,
} from '../lib/labels';
import { useDebounced } from '../lib/useDebounced';

const PAGE = 50;

const auditPageSchema = paginatedSchema(auditLogRowSchema);

interface Filters {
  entityType: string;
  entityId: string;
  actorId: string;
  actorCompanyId: string; // M13: rows whose actor is a firm's ingestion key, not a user
  action: string;
  from: string; // yyyy-mm-dd (native date input); converted to epoch-ms bounds in the query
  to: string;
}

const EMPTY_FILTERS: Filters = {
  entityType: '',
  entityId: '',
  actorId: '',
  actorCompanyId: '',
  action: '',
  from: '',
  to: '',
};

/**
 * Localize a stored actor role through the shared role labels; the audit column is free
 * text (retention-tolerant), so an unrecognized legacy value falls back to the raw string.
 */
function actorRoleLabel(raw: string): string {
  return roleSchema.safeParse(raw).success ? roleLabel(raw as Role) : raw;
}

/**
 * Who acted, as a row renders it: a display line, plus the raw actor id to show beneath it when
 * the two differ. Three cases, and the middle one is why this exists: an ingestion write has NO
 * user actor (`audit_log.actor_id` FKs `users`), so before M13 every firm submission rendered as
 * "system" — indistinguishable from an alarm or a cron sweep.
 *
 * For a user the line prefers the name the API resolved, then the email, then the id itself:
 * `name_first`/`name_last` are nullable, and the API withholds both for a soft-erased account.
 * The id stays visible as the second line because it is what the filter and every deep link use.
 */
function actorLabel(
  row: AuditLogRow,
  firmNames: Map<string, string>,
): { primary: string; id: string | null } {
  if (row.actorId) {
    const primary = row.actorName ?? row.actorEmail ?? row.actorId;
    return { primary, id: primary === row.actorId ? null : row.actorId };
  }
  if (row.actorCompanyId) {
    const name = firmNames.get(row.actorCompanyId) ?? row.actorCompanyId;
    return { primary: t('audit.firmActor', { name }), id: null };
  }
  return { primary: t('audit.systemActor'), id: null };
}

/** The "Kullanıcı" cell: who acted, their role, and the stable id underneath. */
function ActorCell({ row, firmNames }: { row: AuditLogRow; firmNames: Map<string, string> }) {
  const actor = actorLabel(row, firmNames);
  return (
    <TableCell className="text-xs">
      {actor.primary}
      {row.actorRole ? ` (${actorRoleLabel(row.actorRole)})` : ''}
      {actor.id && <span className="block font-mono text-muted-foreground">{actor.id}</span>}
    </TableCell>
  );
}

/** Pretty-print a stored JSON payload; fall back to the raw text if it isn't valid JSON. */
function pretty(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

/**
 * Audit-log viewer (M10) — filterable, paginated read over the append-only trail
 * (`GET /admin/audit`, AuditView = ADMIN/DEVELOPER). Deep-linkable: `/audit?actorId=…` /
 * `?entityType=…&entityId=…` arrive via typed search params (user + sale pages link here).
 * Rows past the KVKK retention horizon have their payloads/IP anonymized — rendered as a dash.
 */
export function AuditLogPage() {
  const initial = useSearch({ strict: false }) as Partial<Filters>;
  const [filters, setFilters] = useState<Filters>({
    ...EMPTY_FILTERS,
    entityType: initial.entityType ?? '',
    entityId: initial.entityId ?? '',
    actorId: initial.actorId ?? '',
    actorCompanyId: initial.actorCompanyId ?? '',
  });
  const [offset, setOffset] = useState(0);
  const [detailsRow, setDetailsRow] = useState<AuditLogRow | null>(null);

  // Deep-link re-sync: TanStack Router keeps this component MOUNTED across a same-route search
  // change (/audit?actorId=A → /audit?entityType=sale&…), so the mount-time initializer alone
  // would leave the inputs (and the table) on the previous filter. Re-apply the deep-linked
  // params whenever they change; hand-typed action/date filters are reset too so the view
  // matches the link that was just followed.
  useEffect(() => {
    setFilters({
      ...EMPTY_FILTERS,
      entityType: initial.entityType ?? '',
      entityId: initial.entityId ?? '',
      actorId: initial.actorId ?? '',
      actorCompanyId: initial.actorCompanyId ?? '',
    });
    setOffset(0);
  }, [initial.entityType, initial.entityId, initial.actorId, initial.actorCompanyId]);

  const debounced = useDebounced(filters);

  const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
  for (const [key, value] of Object.entries(debounced)) {
    if (!value.trim()) continue;
    if (key === 'from') {
      qs.set('from', String(new Date(`${value}T00:00:00`).getTime())); // inclusive day start
    } else if (key === 'to') {
      qs.set('to', String(new Date(`${value}T23:59:59.999`).getTime())); // inclusive day end
    } else {
      qs.set(key, value.trim());
    }
  }
  // The firm directory, so an ingestion row shows a name rather than a bare UUID. Includes
  // ARCHIVED firms: the audit log is history, and a firm's rows outlive it — without them an
  // archived partner's whole trail would degrade to raw UUIDs and drop out of the filter, which
  // is precisely when someone is most likely to be reading it. Cached under its own key so the
  // archived rows never leak into `CompanyPicker`'s active-only `['companies']` list.
  const firms = useQuery({
    queryKey: ['companies', 'withArchived'],
    queryFn: () =>
      api('/admin/companies?includeArchived=1', {}, z.array(companySummarySchema)),
    staleTime: 5 * 60_000,
  });
  const firmNames = new Map((firms.data ?? []).map((f) => [f.id, f.name]));

  const list = useQuery({
    queryKey: ['audit', qs.toString()],
    queryFn: () => api(`/admin/audit?${qs.toString()}`, {}, auditPageSchema),
  });

  const set =
    (key: keyof Filters) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setFilters((f) => ({ ...f, [key]: e.target.value }));
      setOffset(0); // a filter change restarts paging
    };
  const activeCount = Object.values(filters).filter((v) => v.trim()).length;

  return (
    <div className="space-y-4">
      <PageHeader title={t('audit.title')}>
        {activeCount > 0 && (
          <>
            <span className="text-sm text-muted-foreground">
              {t('audit.filter.active', { n: String(activeCount) })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setOffset(0);
              }}
            >
              {t('audit.filter.clear')}
            </Button>
          </>
        )}
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="f-entity-type">{t('audit.filter.entityType')}</Label>
          <Input id="f-entity-type" list="audit-entity-types" value={filters.entityType} onChange={set('entityType')} />
          {/* `label` carries the localized name while `value` stays the stored slug the API
              filters on — so the suggestion list reads in the active language and a hand-typed
              value (the column is free text) still works. */}
          <datalist id="audit-entity-types">
            {AUDIT_ENTITY_TYPES.map((e) => (
              <option key={e} value={e} label={auditEntityTypeLabel(e)} />
            ))}
          </datalist>
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-entity-id">{t('audit.filter.entityId')}</Label>
          <Input id="f-entity-id" value={filters.entityId} onChange={set('entityId')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-actor">{t('audit.filter.actorId')}</Label>
          <Input id="f-actor" value={filters.actorId} onChange={set('actorId')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-firm">{t('audit.filter.actorCompany')}</Label>
          <select
            id="f-firm"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={filters.actorCompanyId}
            onChange={set('actorCompanyId')}
          >
            <option value="">{t('common.all')}</option>
            {(firms.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-action">{t('audit.filter.action')}</Label>
          <Input id="f-action" list="audit-actions" value={filters.action} onChange={set('action')} />
          <datalist id="audit-actions">
            {AUDIT_ACTIONS.map((a) => (
              <option key={a} value={a} label={auditActionLabel(a)} />
            ))}
          </datalist>
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-from">{t('audit.filter.from')}</Label>
          <Input id="f-from" type="date" value={filters.from} onChange={set('from')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-to">{t('audit.filter.to')}</Label>
          <Input id="f-to" type="date" value={filters.to} onChange={set('to')} />
        </div>
      </div>

      {list.isLoading ? (
        <SkeletonRows rows={8} />
      ) : list.isError ? (
        <ErrorMsg error={list.error} />
      ) : list.data && list.data.items.length > 0 ? (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('audit.col.time')}</TableHead>
                <TableHead>{t('audit.col.actor')}</TableHead>
                <TableHead>{t('audit.col.entity')}</TableHead>
                <TableHead>{t('audit.col.action')}</TableHead>
                <TableHead>{t('audit.col.details')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data.items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap tabular-nums">{formatDateTime(row.occurredAt)}</TableCell>
                  <ActorCell row={row} firmNames={firmNames} />
                  <TableCell className="text-xs">
                    {auditEntityTypeLabel(row.entityType)}
                    <span className="block font-mono text-muted-foreground">{row.entityId}</span>
                  </TableCell>
                  <TableCell>{auditActionLabel(row.action)}</TableCell>
                  <TableCell>
                    {row.beforeData || row.afterData ? (
                      // The JSON payloads open in a dialog (redesign-admin.md §7) — no more
                      // tall <pre> blocks inflating table rows.
                      <Button variant="ghost" size="sm" onClick={() => setDetailsRow(row)}>
                        {t('audit.expand')}
                      </Button>
                    ) : (
                      // Never written, or anonymized by the KVKK retention sweep (ADR 0022).
                      <span className="text-xs text-muted-foreground">{t('audit.redacted')}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination offset={offset} limit={PAGE} total={list.data.total} onOffsetChange={setOffset} />
        </>
      ) : (
        <EmptyState icon={ScrollText} title={t('audit.empty')} />
      )}

      {detailsRow && (
        <Dialog open onOpenChange={(o) => !o && setDetailsRow(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {t('audit.detailsTitle')} — {auditActionLabel(detailsRow.action)}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-3 md:grid-cols-2">
              {detailsRow.beforeData && (
                <div className="min-w-0">
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    {t('audit.before')}
                  </div>
                  <pre className="max-h-96 overflow-auto rounded bg-sunken p-2 text-xs">
                    {pretty(detailsRow.beforeData)}
                  </pre>
                </div>
              )}
              {detailsRow.afterData && (
                <div className="min-w-0">
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    {t('audit.after')}
                  </div>
                  <pre className="max-h-96 overflow-auto rounded bg-sunken p-2 text-xs">
                    {pretty(detailsRow.afterData)}
                  </pre>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
